import { ComponentType } from '@vgtv/api-client/lib/component';

import { ViewportType } from '../../utils/viewport/getViewportType';
import {
  COMPONENT_TYPE_GALLERY,
  COMPONENT_TYPE_LIST_IN_POPUP,
} from '../../consts';

import { getComponentItemsCount } from './components/getComponentItemsCount';

import type { DataType, Layout } from '@vgtv/api-client/lib/component';

// the lowest number of items to fetch for a given component type
const minimums: Partial<Record<ExtendedComponentType, number>> = {
  [ComponentType.SLIDER]: 10,
  [ComponentType.CONTINUE_WATCHING]: 10,
};

// e.g. fetch x lines for lists / x times what's visible in viewport for sliders
const multipliers: Partial<
  Record<ExtendedComponentType, Record<ViewportType, number>>
> = {
  [ComponentType.SLIDER]: {
    [ViewportType.MOBILE]: 2,
    [ViewportType.TABLET]: 2,
    [ViewportType.DESKTOP]: 4,
  },
  [ComponentType.CONTINUE_WATCHING]: {
    [ViewportType.MOBILE]: 2,
    [ViewportType.TABLET]: 2,
    [ViewportType.DESKTOP]: 4,
  },
  [ComponentType.LIST]: {
    [ViewportType.MOBILE]: 2,
    [ViewportType.TABLET]: 2,
    [ViewportType.DESKTOP]: 2,
  },
  [ComponentType.FILTERED_LIST]: {
    [ViewportType.MOBILE]: 2,
    [ViewportType.TABLET]: 2,
    [ViewportType.DESKTOP]: 2,
  },
  [COMPONENT_TYPE_LIST_IN_POPUP]: {
    [ViewportType.MOBILE]: 4,
    [ViewportType.TABLET]: 4,
    [ViewportType.DESKTOP]: 4,
  },
  [COMPONENT_TYPE_GALLERY]: {
    [ViewportType.MOBILE]: 5,
    [ViewportType.TABLET]: 6,
    [ViewportType.DESKTOP]: 5,
  },
};

export type ExtendedComponentType =
  | ComponentType
  | typeof COMPONENT_TYPE_LIST_IN_POPUP
  | typeof COMPONENT_TYPE_GALLERY;

/**
 * Returns the number of items which should be fetched for a given component and viewport.
 */
function getComponentDataFetchLimit({
  componentType,
  dataType,
  componentLayout,
  viewportType,
}: {
  componentType: ExtendedComponentType;
  dataType: DataType;
  componentLayout?: Layout;
  viewportType: ViewportType;
}): number | undefined {
  const itemsCount = getComponentItemsCount({
    componentType:
      componentType === COMPONENT_TYPE_LIST_IN_POPUP
        ? ComponentType.LIST
        : componentType,
    componentLayout,
    dataType,
  });

  if (itemsCount === undefined) {
    return undefined;
  }

  const minimum = minimums[componentType] || 0;

  const multiplier = multipliers[componentType]?.[viewportType] ?? 1;

  return Math.max(Math.ceil(itemsCount * multiplier), minimum);
}

export { getComponentDataFetchLimit };
